import useGTMDispatch from "@src/app/hooks/useGTMDispatch";
import viewportState from "@src/app/stores/viewport";
import type { BFFArticleCard } from "@src/app/types/bff";
import type { MouseEvent } from "react";
import { useSnapshot } from "valtio";

import ArticleCard from "../ArticleCard";

interface HeroGridProps {
  articles: BFFArticleCard[];
}

export default function HeroGrid({ articles }: HeroGridProps) {
  const { isMobileView, isTabletView } = useSnapshot(viewportState);
  const sendDataToGTM = useGTMDispatch();
  if (articles.length === 0) return null;

  const [mainArticle, ...sideArticles] = articles;
  const bannerEventByPath: Record<string, string> = {};
  articles.slice(0, 3).forEach((article, index) => {
    const key = article.urlPath || "";
    if (!key) return;
    bannerEventByPath[key] = `homepage banner ${index + 1}`;
  });

  const handleBannerClickCapture = (event: MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement | null;
    const anchor = target?.closest("a");
    if (!anchor) return;
    const href = anchor.getAttribute("href") || "";
    if (!href) return;

    const matchedPath = Object.keys(bannerEventByPath).find((path) =>
      href.includes(path),
    );
    if (!matchedPath) return;

    sendDataToGTM({
      event: "tcCustomEvent",
      eventCategory: bannerEventByPath[matchedPath],
      eventAction: "click",
      eventLabel: matchedPath.split("?")[0] || matchedPath,
    });
  };

  return (
    <div
      className="grid grid-cols-1 lg:grid-cols-2"
      onClickCapture={handleBannerClickCapture}
    >
      <ArticleCard
        article={mainArticle}
        descriptionLineClamp={isMobileView || isTabletView ? 3 : 6}
        titleVariant="header-2"
        hideDate
      />

      <div className="grid grid-cols-1 lg:grid-cols-2">
        {sideArticles.slice(0, 4).map((article) => (
          <ArticleCard key={article.id} article={article} hideDate />
        ))}
      </div>
    </div>
  );
}
